import React, { useState } from 'react'
import { TouchableOpacity, View } from 'react-native'
import { PopoverPlacements, Tooltip } from '@ui-kitten/components/ui'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, Text } from '@src/components/base'
import { ActionGoalSummary } from '@src/types'
import { useIsDarkMode } from '@src/config/theme'
import { useGetActionGoalWeeklyProgress } from '../hooks'
import { ActionGoalProgressArc } from './ActionGoalProgressArc'

interface ActionGoalProgressProps {
  summaries?: ActionGoalSummary[]
  hideDescription?: boolean
}

export const ActionGoalProgress = ({ summaries, hideDescription }: ActionGoalProgressProps) => {
  const styles = useStyleSheet(themedStyles)
  const weeklyProgress = useGetActionGoalWeeklyProgress(summaries)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const isDarkMode = useIsDarkMode()

  const arcDisplayValue =
    weeklyProgress.numberDaysTotal > 0
      ? Math.min(
          (weeklyProgress.numberDaysCompleted / weeklyProgress.numberDaysTotal) * 100,
          100,
        ).toFixed(0) + '%'
      : '0%'
  const titleText = `${weeklyProgress.numberDaysCompleted} of ${weeklyProgress.numberDaysTotal} Completed`
  const tooltip = (
    <Tooltip
      placement={PopoverPlacements.TOP_START}
      visible={tooltipVisible}
      onBackdropPress={() => setTooltipVisible(false)}
      style={styles.tooltipContainer}
      anchor={() => (
        <TouchableOpacity
          accessibilityLabel="Action Goal Progress Info"
          onPress={() => setTooltipVisible(!tooltipVisible)}
          hitSlop={{ bottom: 10, top: 10, left: 10, right: 10 }}
        >
          <Icon
            name="info"
            size={16}
            weight={isDarkMode ? 'fill' : 'regular'}
            style={styles.infoIcon}
          />
        </TouchableOpacity>
      )}
    >
      <Text type="small" style={styles.tooltipText}>
        Each goal lets you set how many days you want to complete it each week. Your progress shows
        the number of days you've completed ({weeklyProgress.numberDaysCompleted}) out of your
        weekly target ({weeklyProgress.numberDaysTotal}).
      </Text>
    </Tooltip>
  )

  return (
    <View style={styles.container}>
      <ActionGoalProgressArc
        value={weeklyProgress.numberDaysCompleted}
        displayValue={arcDisplayValue}
        minValue={0}
        maxValue={weeklyProgress.numberDaysTotal}
      />
      <View style={styles.titleContainer}>
        <Text style={styles.title} bold type="large">
          {titleText}
        </Text>
        {weeklyProgress.numberDaysTotal > 0 && tooltip}
      </View>
      {!hideDescription && (
        <Text style={styles.subTitle} type="regular">
          This week's progress
        </Text>
      )}
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  tooltipContainer: {
    maxWidth: 250,
    opacity: 0.95,
    backgroundColor: 'theme.tooltip.background',
  },
  tooltipText: {
    color: 'theme.tooltip.textPrimary',
  },
  infoIcon: {
    marginLeft: 2,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    textAlign: 'center',
  },
  subTitle: {
    textAlign: 'center',
    color: 'theme.text.secondary',
  },
})
